import { ArrowUp } from 'lucide-react';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatLengthWithBasis, roundTo6dp } from '~/utils';
import {
  getGradeInformation,
  getEffectivePricePerPiece,
  getListedPricePerPiece,
  getCartLinePrice,
  getMarketComparisonPrice,
  getListedPricePerUnit,
  getPlatformProductDimensions,
  getVehiclePayload,
  type CartOrderItem,
} from '~/utils/order';
import { usePreferredUnit } from '~/hooks/usePreferredUnit';
import type { UnitCode } from '~/graphql/graphql';
import { useItemWithQuantityOverride } from '~/hooks/useQuantityOverrides';
import {
  convertPricePerUnit,
  pricedUnitFor,
  hasDimensions,
  toStandardUnits,
  unitsPerPieceOrNull,
} from '~/utils/unitConversion';
import {
  bundleSizeInUnit,
  computeTopUpQuantity,
  toStoragePrice,
} from './cartItemPricing';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput.tsx';
import { CartItemHeader } from './CartItemHeader';
import { CartItemLineTotal } from './CartItemLineTotal';
import { CartItemMarketPrice } from './CartItemMarketPrice';
import { CartItemPriceSummary } from './CartItemPriceSummary';
import { CartItemSuggestPricePopover } from './CartItemSuggestPricePopover';
import type { TransportMode } from '~/hooks/useTransportState';
import {
  useCartItemMutations,
  type CartListItemHandle,
} from './useCartItemMutations';

export type { CartListItemHandle } from './useCartItemMutations';

interface CartItemProps {
  item: CartOrderItem;
  cartId: string;
  locationId: string;
  // Undefined when no usable vehicle is selected; labels fall back to transport-agnostic copy.
  transport: TransportMode | undefined;
  totalLoads: number;
  selectedTransportVehicleId: string;
  sellingOrganizationFacilityId: string;
  parentOrderId?: string | null;
  isFromQuote?: boolean;
  currency?: string | null;
  incoTerm?: string | null;
}

export const CartListItem = forwardRef<CartListItemHandle, CartItemProps>(
  function CartListItem(
    {
      item: serverItem,
      cartId,
      locationId,
      transport,
      totalLoads,
      selectedTransportVehicleId,
      sellingOrganizationFacilityId,
      parentOrderId,
      isFromQuote = false,
      currency: cartCurrency,
      incoTerm,
    }: Readonly<CartItemProps>,
    ref
  ) {
    const item = useItemWithQuantityOverride(cartId, serverItem);
    const [showSuggestPrice, setShowSuggestPrice] = useState(false);
    const [suggestedPrice, setSuggestedPrice] = useState(
      item.suggestedPrice != null ? String(item.suggestedPrice.amount) : ''
    );
    const [prevItemSuggestedPrice, setPrevItemSuggestedPrice] = useState(
      item.suggestedPrice
    );
    // Sync with server when preferred unit changes; the resolver converts the
    // stored standard-unit price to the display unit, as it does for item.price.
    if (item.suggestedPrice !== prevItemSuggestedPrice) {
      setSuggestedPrice(
        item.suggestedPrice != null ? String(item.suggestedPrice.amount) : ''
      );
      setPrevItemSuggestedPrice(item.suggestedPrice);
    }
    const [draftPrice, setDraftPrice] = useState('');
    const suggestedPriceNum = parseFloat(suggestedPrice) || 0;

    const [buyCode, setBuyCode] = useState(item.buyCode ?? '');
    const [prevItemBuyCode, setPrevItemBuyCode] = useState(item.buyCode);
    if (item.buyCode !== prevItemBuyCode) {
      setBuyCode(item.buyCode ?? '');
      setPrevItemBuyCode(item.buyCode);
    }

    const { t } = useTranslation('cart');

    const vehiclePayload = getVehiclePayload(
      item.platformProductsLengths.platformProductVehicleCapacities,
      selectedTransportVehicleId
    );

    const currency = cartCurrency ?? undefined;
    const category = item.platformProductsLengths.platformProduct.category;
    const displayUnit = usePreferredUnit(category, item.quantity.unit);
    const dims = getPlatformProductDimensions(
      item.platformProductsLengths.platformProduct,
      item.platformProductsLengths.length,
      item.platformProductsLengths.isNominal
    );
    const { widthInches, thicknessInches, lengthInches } = dims;
    const hasDims = hasDimensions(dims);

    const suggestedAmount = item.suggestedPrice?.amount ?? null;
    const sellerPriceAmount = getListedPricePerUnit(item, incoTerm).amount;
    const effectiveAmount = getCartLinePrice(item, incoTerm);

    const displayPricePerUnit = convertPricePerUnit(
      effectiveAmount,
      displayUnit,
      item.quantity.unit,
      dims
    );
    const sellerDisplayPricePerUnit = convertPricePerUnit(
      sellerPriceAmount,
      displayUnit,
      item.quantity.unit,
      dims
    );
    const marketBasisPricePerUnit = convertPricePerUnit(
      getMarketComparisonPrice(
        {
          ...item,
          suggestedPrice:
            suggestedPriceNum > 0
              ? {
                  amount: suggestedPriceNum,
                  currency: item.suggestedPrice?.currency ?? currency ?? 'USD',
                }
              : null,
        },
        incoTerm
      ),
      displayUnit,
      item.quantity.unit,
      dims
    );
    const priceUnit = pricedUnitFor(displayUnit, item.quantity.unit, dims);
    const canConvert = priceUnit !== item.quantity.unit;
    const displayQuantity = canConvert
      ? toStandardUnits(
          item.quantity.value,
          item.quantity.unit,
          displayUnit,
          lengthInches,
          widthInches,
          thicknessInches
        )
      : item.quantity.value;

    const piecesPerBundle = item.effectivePiecesPerBundle;
    const standardUnit =
      item.platformProductsLengths.platformProduct.standardUnitsCode;

    const listedPricePerPiece = getListedPricePerPiece(item, incoTerm);
    const pricePerPiece =
      listedPricePerPiece != null
        ? roundTo6dp(listedPricePerPiece.amount)
        : null;

    const piecesCount: number = item.quantityStored?.value ?? 0;
    const unitsPerPieceIn = (unit: UnitCode) =>
      (hasDims || unit === 'PCS') && piecesPerBundle > 0
        ? unitsPerPieceOrNull(unit, dims)
        : null;
    const uppPrice = unitsPerPieceIn(priceUnit);

    // The backend value is absent for unsaved edits; compute the suggestion client-side.
    const rawEffectivePricePerPiece: number | null =
      item.suggestedPricePerPiece == null &&
      suggestedPriceNum > 0 &&
      uppPrice != null
        ? suggestedPriceNum * uppPrice
        : (getEffectivePricePerPiece(item, incoTerm)?.amount ?? null);
    const effectivePricePerPiece =
      rawEffectivePricePerPiece != null
        ? roundTo6dp(rawEffectivePricePerPiece)
        : null;

    const draftPricePerPiece: number | null = (() => {
      const draft = parseFloat(draftPrice);
      if (!(draft > 0)) return null;
      if (priceUnit === 'PCS') return roundTo6dp(draft);
      return uppPrice != null ? roundTo6dp(draft * uppPrice) : null;
    })();

    // The buyer types and reads prices per priceUnit; storage is per the
    // product's standard unit, and the resolver converts back on the way out.
    // Anything sent to the server has to be converted exactly once.
    const toStorageString = (typedPrice: string) => {
      const parsed = parseFloat(typedPrice);
      if (!Number.isFinite(parsed)) return '';
      return String(toStoragePrice(parsed, priceUnit, standardUnit, dims));
    };

    const bundleSizeInItemUnit = bundleSizeInUnit(
      piecesPerBundle,
      item.quantity.unit,
      dims
    );

    const topUpQty = computeTopUpQuantity({
      quantity: item.quantity,
      platformProductsLengths: item.platformProductsLengths,
      vehiclePayload,
      totalLoads,
      bundleSize: bundleSizeInItemUnit,
      dims,
    });

    const canTopUp = topUpQty > item.quantity.value && transport;

    const topUpTitle = canTopUp
      ? t('cart.top_up_title_with_qty', {
          quantity: (topUpQty - item.quantity.value).toFixed(2),
          unit: item.quantity.unit,
          number: Math.ceil(totalLoads),
          transportations: {
            truck: t('cart.trucks_lowercase'),
            railcar: t('cart.railcars_lowercase'),
            container: t('cart.containers_lowercase'),
          }[transport],
        })
      : t('cart.top_up_title');

    const {
      deletingCart,
      savePriceSuggestion,
      onUpdateQuantity,
      onUpdateBuyCode,
      flushBuyCode,
      onUpdatePiecesPerBundle,
      onRemoveItem,
      onCapacitySubmit,
    } = useCartItemMutations({
      item,
      cartId,
      locationId,
      sellingOrganizationFacilityId,
      parentOrderId,
      selectedTransportVehicleId,
      suggestedPriceInStorageUnit: toStorageString(suggestedPrice),
      priceUnit,
      uppPrice,
      bundleSize: bundleSizeInItemUnit,
      dims,
      handleRef: ref,
    });

    const onTopUp = () => {
      onUpdateQuantity(item.platformProductsLengths.id, topUpQty);
    };

    return (
      <div
        data-testid="cart-line"
        className="p-4 sm:p-6 border-b border-neut-200 last:border-b-0"
      >
        <div className="flex gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-warning-50 to-warning-100 rounded-lg flex items-center justify-center shrink-0">
            <div className="text-2xl">🌲</div>
          </div>

          <div className="flex-1 min-w-0">
            <CartItemHeader
              name={item.platformProductsLengths.platformProduct.name}
              gradeInformation={getGradeInformation(
                item.platformProductsLengths
              )}
              lengthDisplayValue={formatLengthWithBasis(
                item.platformProductsLengths.lengthDisplayValue,
                item.platformProductsLengths.isNominal
              )}
              isDeleting={deletingCart}
              onRemove={() => onRemoveItem(item.platformProductsLengths.id)}
            />

            <fieldset disabled={isFromQuote} className="min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-start gap-2">
                  <BundleQuantityInput
                    onBundleChange={(itemQuantity) => {
                      onUpdateQuantity(
                        item.platformProductsLengths.id,
                        itemQuantity.value
                      );
                    }}
                    quantity={item.quantity}
                    platformProductsLength={item.platformProductsLengths}
                    disabled={deletingCart}
                    vehiclePayload={vehiclePayload}
                    vehicleLabel={transport}
                    onCapacitySubmit={onCapacitySubmit}
                    piecesPerBundle={piecesPerBundle}
                    onPiecesPerBundleChange={(value, quantity) =>
                      onUpdatePiecesPerBundle(
                        item.platformProductsLengths.id,
                        value,
                        quantity
                      )
                    }
                  />
                  {canTopUp && (
                    <button
                      type="button"
                      onClick={onTopUp}
                      className="px-2 py-1 rounded-md border border-prim-400 bg-prim-400/10 hover:bg-prim-400/20 transition-colors flex items-center gap-1 text-xs font-medium text-prim-400"
                      title={topUpTitle}
                    >
                      <ArrowUp className="w-3 h-3" aria-hidden="true" />
                      {t('cart.top_up')}
                    </button>
                  )}
                </div>
                <div className="text-right">
                  <div className="flex items-end justify-end mb-0.5 relative">
                    <CartItemPriceSummary
                      displayPricePerUnit={displayPricePerUnit}
                      effectivePricePerPiece={effectivePricePerPiece}
                      sellerDisplayPricePerUnit={sellerDisplayPricePerUnit}
                      suggestedAmount={suggestedAmount}
                      canClearSuggestion={item.price != null}
                      priceUnit={priceUnit}
                      currency={currency}
                      onEditSuggestion={() => {
                        setDraftPrice(
                          suggestedPrice
                            ? parseFloat(suggestedPrice).toFixed(2)
                            : ''
                        );
                        setShowSuggestPrice(true);
                      }}
                      onClearSuggestion={() => {
                        const previous = suggestedPrice;
                        setSuggestedPrice('');
                        void savePriceSuggestion(
                          item.platformProductsLengths.id,
                          item.quantity,
                          null
                        ).then((saved) => {
                          if (!saved) setSuggestedPrice(previous);
                        });
                      }}
                      onStartSuggestion={() => {
                        setDraftPrice('');
                        setShowSuggestPrice((v) => !v);
                      }}
                    />
                    {showSuggestPrice && (
                      <CartItemSuggestPricePopover
                        sellerDisplayPricePerUnit={sellerDisplayPricePerUnit}
                        pricePerPiece={pricePerPiece}
                        draftPrice={draftPrice}
                        draftPricePerPiece={draftPricePerPiece}
                        priceUnit={priceUnit}
                        currency={currency}
                        onDraftPriceChange={setDraftPrice}
                        onCancel={() => setShowSuggestPrice(false)}
                        onConfirm={() => {
                          const previous = suggestedPrice;
                          setSuggestedPrice(draftPrice);
                          setShowSuggestPrice(false);
                          void savePriceSuggestion(
                            item.platformProductsLengths.id,
                            item.quantity,
                            toStorageString(draftPrice)
                          ).then((saved) => {
                            if (!saved) setSuggestedPrice(previous);
                          });
                        }}
                      />
                    )}
                  </div>
                  <CartItemMarketPrice
                    marketMid={item.platformProductsLengths.latestPriceMid}
                    displayUnit={displayUnit}
                    standardUnit={standardUnit}
                    dims={dims}
                    displayPricePerUnit={marketBasisPricePerUnit}
                    priceUnit={priceUnit}
                    currency={currency}
                  />
                  <CartItemLineTotal
                    displayPricePerUnit={displayPricePerUnit}
                    rawPricePerPiece={rawEffectivePricePerPiece}
                    piecesCount={piecesCount}
                    displayQuantity={displayQuantity}
                    currency={currency}
                  />
                </div>
              </div>
              <div className="mt-3">
                <label
                  htmlFor={`buycode-${item.platformProductsLengths.id}`}
                  className="block text-xs font-medium text-neut-600 mb-1"
                >
                  {t('cart.buycode_label')}
                </label>
                <input
                  id={`buycode-${item.platformProductsLengths.id}`}
                  type="text"
                  maxLength={255}
                  data-testid="buycode-input"
                  value={buyCode}
                  disabled={deletingCart}
                  placeholder={t('cart.buycode_placeholder')}
                  onChange={(e) => {
                    setBuyCode(e.target.value);
                    onUpdateBuyCode(
                      item.platformProductsLengths.id,
                      e.target.value
                    );
                  }}
                  onBlur={() => flushBuyCode()}
                  className="w-full sm:w-64 px-2 py-1 text-sm border border-neut-200 rounded-md focus:outline-none focus:ring-1 focus:ring-prim-400"
                />
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    );
  }
);

CartListItem.displayName = 'CartListItem';
